import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Copy, Download, FileCode2, KeyRound, RefreshCcw, AlertCircle, ArrowRight, Lock } from "lucide-react";
import { downloadAsset, fetchDownloadAssets, generateNewLicense } from "../api/client";
import { useAuth } from "../context/AuthContext";
import LockedFeatureGate from "../components/LockedFeatureGate";

// ── Reusable Upgrade Plan Popup ─────────────────────────────────────────────
export function UpgradePlanModal({ show, onClose, navigate, planName, reason, limits }) {
  if (!show) return null;
  return (
    <div className="checkout-modal-overlay">
      <div className="checkout-modal" style={{ maxWidth: "440px" }}>
        <div className="checkout-header">
          <h4 className="checkout-title" style={{ color: "var(--red)" }}>Upgrade Plan Required</h4>
          <button className="close-modal-btn" onClick={onClose}>✕</button>
        </div>
        <div className="checkout-content" style={{ textAlign: "center", padding: "30px 24px" }}>
          <div className="upgrade-warning-icon">
            <AlertCircle size={30} />
          </div>
          <h3 style={{ fontSize: "18px", fontWeight: "800", color: "var(--text)", marginBottom: "12px" }}>
            Plan Limit Reached
          </h3>
          <p style={{ color: "var(--text2)", fontSize: "14px", lineHeight: "1.6", marginBottom: "20px" }}>
            {reason || <>You have reached the maximum allowed limit for your <strong>{planName?.toUpperCase() || "DEMO"}</strong> package.</>}
          </p>
          {limits && (
            <div style={{ background: "rgba(255,255,255,0.02)", border: "1px solid var(--border)", borderRadius: "12px", padding: "16px", marginBottom: "20px", textAlign: "left" }}>
              <div style={{ fontSize: "12px", fontWeight: "700", color: "var(--accent)", marginBottom: "8px", textTransform: "uppercase", letterSpacing: "1px" }}>
                Current Package Limits
              </div>
              <div style={{ fontSize: "12.5px", color: "var(--text2)", display: "flex", flexDirection: "column", gap: "6px" }}>
                {limits.map((l, i) => (
                  <div key={i}>{l.icon} {l.label}: <strong style={{ color: "var(--text)" }}>{l.value}</strong></div>
                ))}
              </div>
            </div>
          )}
          <p style={{ fontSize: "12px", color: "var(--text2)", marginBottom: "20px", lineHeight: "1.5" }}>
            Upgrade your plan to unlock more license IDs, MT5 connections, and premium support channels.
          </p>
          <button
            className="btn btn-primary"
            style={{ width: "100%", justifyContent: "center", padding: "12px" }}
            onClick={() => { onClose(); navigate("/renew-plan"); }}
          >
            View Upgrade Plans <ArrowRight size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Downloads() {
  const navigate = useNavigate();
  const { user } = useAuth();

  // ── ALL HOOKS FIRST — before any conditional returns ─────────────────────
  const [data, setData]               = useState({ assets: [], license_ids: [], allowed_licenses: 1 });
  const [loading, setLoading]         = useState(true);
  const [toast, setToast]             = useState(null);
  const [licenseMode, setLicenseMode] = useState("auto");
  const [customLicenseId, setCustomLicenseId] = useState("");
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  const handleCustomLicenseChange = (e) => {
    let inputVal = e.target.value.toUpperCase();
    if (!inputVal.startsWith("TVMT5-")) {
      inputVal = "TVMT5-";
    }
    let suffix = inputVal.substring(6).replace(/[^A-Z0-9]/g, "");
    // Enforce exactly up to 12 alphanumeric characters so total length with TVMT5 (5 chars) is exactly 17 characters (5-4-4-4)
    if (suffix.length > 12) {
      suffix = suffix.substring(0, 12);
    }
    const grouped = suffix.match(/.{1,4}/g)?.join("-") || suffix;
    setCustomLicenseId("TVMT5-" + grouped);
  };

  const isExpired   = user?.service_end ? new Date() > new Date(user.service_end) : false;
  const hasNoAccess = user?.role !== "admin" && (isExpired || user?.allowed_licenses === 0);

  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 2500);
  };

  const load = () => {
    setLoading(true);
    fetchDownloadAssets()
      .then(setData)
      .catch((err) => showToast(err.response?.data?.detail || "Downloads load failed", "error"))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  // ── Access gate AFTER all hooks ──────────────────────────────────────────
  if (hasNoAccess) {
    return <LockedFeatureGate featureName="EA Downloads & License Manager" navigate={navigate} />;
  }

  const copy = async (value) => {
    await navigator.clipboard.writeText(value);
    showToast("Copied to clipboard");
  };

  const activeLicenses = data.license_ids?.length || 0;
  const allowedLicenses = data.allowed_licenses || user?.allowed_licenses || 1;

  const handleGenerateLicense = () => {
    if (activeLicenses >= allowedLicenses) {
      setShowUpgradeModal(true);
      return;
    }
    if (licenseMode === "manual") {
      if (!customLicenseId.trim()) {
        showToast("Please enter a custom license ID", "error");
        return;
      }
      const cleanLen = customLicenseId.replace(/[^A-Z0-9]/g, "").length;
      if (cleanLen !== 17) {
        showToast("License ID must have exactly 17 letters/digits in total (prefix TVMT5 plus 12 suffix characters, structured as 5-4-4-4)", "error");
        return;
      }
    }
    setLoading(true);
    const payload = {
      mode: licenseMode,
      custom_license_id: licenseMode === "manual" ? customLicenseId.trim().toUpperCase() : undefined
    };
    generateNewLicense(payload)
      .then((res) => {
        showToast(res.message || "License generated successfully!");
        setCustomLicenseId("");
        setLicenseMode("auto");
        load();
      })
      .catch((err) => {
        const detail = err.response?.data?.detail || "Failed to generate license";
        // If backend also says limit reached, show upgrade modal
        if (err.response?.status === 400 && detail.toLowerCase().includes("limit")) {
          setShowUpgradeModal(true);
        } else {
          showToast(detail, "error");
        }
      })
      .finally(() => setLoading(false));
  };

  return (
    <div>
      {toast && <div className={`toast ${toast.type}`}>{toast.msg}</div>}

      <div className="page-header">
        <div>
          <div className="page-title">EA Downloads</div>
          <div className="page-sub">Download MT5 EA, sample Pine Script, and manage license IDs for your clients</div>
        </div>
        <button className="btn btn-ghost" onClick={load} disabled={loading}>
          <RefreshCcw size={15} />
          Refresh
        </button>
      </div>

      <div className="grid-2">
        {/* Download Files Card */}
        <div className="card tool-card">
          <div className="card-header">
            <span>Download Files</span>
            <FileCode2 size={16} />
          </div>
          {loading ? (
            <div className="loading">Loading downloads...</div>
          ) : (
            <div className="stack-list">
              {data.assets.map((asset) => (
                <div className="download-row" key={asset.key}>
                  <div>
                    <div className="download-title">{asset.label}</div>
                    <div className="download-sub">{asset.filename}</div>
                  </div>
                  <button
                    className="btn btn-primary"
                    disabled={!asset.available}
                    onClick={() => downloadAsset(asset.key, asset.filename)}
                  >
                    <Download size={15} />
                    {asset.available ? "Download" : "Missing"}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* License ID Manager Card */}
        <div className="card tool-card">
          <div className="card-header">
            <span>
              License IDs
              <span style={{
                marginLeft: "8px", fontSize: "11px", fontWeight: "700",
                color: activeLicenses >= allowedLicenses ? "var(--red)" : "var(--green)",
                background: activeLicenses >= allowedLicenses ? "rgba(239,68,68,0.1)" : "rgba(16,185,129,0.1)",
                border: `1px solid ${activeLicenses >= allowedLicenses ? "rgba(239,68,68,0.2)" : "rgba(16,185,129,0.2)"}`,
                padding: "2px 8px", borderRadius: "10px"
              }}>
                {activeLicenses} / {allowedLicenses} used
              </span>
            </span>
            <KeyRound size={16} />
          </div>
          <div className="stack-list">
            {(data.license_ids || []).length === 0 && (
              <div style={{ color: "var(--text2)", fontSize: "13px", textAlign: "center", padding: "16px 0" }}>
                No license IDs yet. Generate your first one below.
              </div>
            )}
            {(data.license_ids || []).map((license) => (
              <div className="license-row" key={license}>
                <code>{license}</code>
                <button className="icon-btn" type="button" onClick={() => copy(license)} title="Copy license">
                  <Copy size={15} />
                </button>
              </div>
            ))}

            {/* License generation section */}
            <div style={{ marginTop: 20, borderTop: "1px solid var(--border)", paddingTop: 20 }}>
              {activeLicenses >= allowedLicenses ? (
                /* Limit reached — show locked state */
                <div style={{
                  background: "rgba(239,68,68,0.05)", border: "1px solid rgba(239,68,68,0.2)",
                  borderRadius: "12px", padding: "20px", textAlign: "center"
                }}>
                  <Lock size={22} style={{ color: "var(--red)", marginBottom: "10px" }} />
                  <div style={{ fontSize: "13px", fontWeight: "700", color: "var(--text)", marginBottom: "6px" }}>
                    License Limit Reached
                  </div>
                  <div style={{ fontSize: "12px", color: "var(--text2)", marginBottom: "16px", lineHeight: "1.5" }}>
                    Your <strong>{user?.service_plan?.toUpperCase() || "DEMO"}</strong> plan allows <strong>{allowedLicenses}</strong> license {allowedLicenses === 1 ? "ID" : "IDs"}.
                    Upgrade to generate more.
                  </div>
                  <button
                    className="btn btn-primary"
                    style={{ width: "100%", justifyContent: "center", fontSize: "13px" }}
                    onClick={() => navigate("/renew-plan")}
                  >
                    Upgrade Plan <ArrowRight size={14} />
                  </button>
                </div>
              ) : (
                /* Can still generate */
                <>
                  <div style={{ fontSize: 11, fontWeight: 700, color: "var(--text2)", marginBottom: 8, textTransform: "uppercase", letterSpacing: "1px" }}>
                    License Generation Mode
                  </div>
                  <div style={{ display: "flex", gap: 10, marginBottom: 16 }}>
                    <button
                      type="button"
                      className={`btn ${licenseMode === "auto" ? "btn-primary" : "btn-ghost"}`}
                      style={{ flex: 1, justifyContent: "center", padding: "8px 16px" }}
                      onClick={() => { setLicenseMode("auto"); setCustomLicenseId(""); }}
                    >
                      Auto Random
                    </button>
                    <button
                      type="button"
                      className={`btn ${licenseMode === "manual" ? "btn-primary" : "btn-ghost"}`}
                      style={{ flex: 1, justifyContent: "center", padding: "8px 16px" }}
                      onClick={() => { setLicenseMode("manual"); setCustomLicenseId("TVMT5-"); }}
                    >
                      Manual ID
                    </button>
                  </div>

                  {licenseMode === "manual" && (
                    <div className="form-group" style={{ marginBottom: 16 }}>
                      <label className="form-label" style={{ fontSize: 11, color: "var(--accent)" }}>Custom License ID</label>
                      <input
                        className="form-input"
                        placeholder="e.g. VIP-CLIENT-50"
                        value={customLicenseId}
                        onChange={handleCustomLicenseChange}
                        style={{ textTransform: "uppercase", fontFamily: "var(--mono)" }}
                        disabled={loading}
                      />
                      <small style={{ color: "var(--text2)", display: "block", marginTop: 4, fontSize: 11 }}>
                        Must be 4–32 characters starting with TVMT5- and separated by dashes.
                      </small>
                    </div>
                  )}

                  <button
                    type="button"
                    className="btn btn-primary"
                    style={{ width: "100%", justifyContent: "center", marginTop: 8 }}
                    onClick={handleGenerateLicense}
                    disabled={loading || (licenseMode === "manual" && !customLicenseId.trim())}
                  >
                    🔑 {licenseMode === "auto" ? "Generate Auto License ID" : "Create Custom License ID"}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Upgrade Plan Modal */}
      <UpgradePlanModal
        show={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        navigate={navigate}
        planName={user?.service_plan}
        limits={[
          { icon: "🔑", label: "License IDs Allowed", value: `${activeLicenses} / ${allowedLicenses}` },
          { icon: "📡", label: "MT5 Connections / License", value: user?.mt5_per_license_allowed || 1 },
          { icon: "📊", label: "TV Strategies", value: user?.num_strategies_allowed || 0 },
        ]}
      />
    </div>
  );
}
